import Lenis from 'lenis'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export { gsap, ScrollTrigger }

export const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const isFinePointer =
  typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches

let lenis = null

/** 惯性平滑滚动，并与 GSAP ScrollTrigger 共用同一帧循环 */
export function initSmoothScroll () {
  if (prefersReducedMotion || lenis) return
  lenis = new Lenis({ lerp: 0.095, wheelMultiplier: 1, smoothWheel: true })
  lenis.on('scroll', ScrollTrigger.update)
  gsap.ticker.add((time) => lenis.raf(time * 1000))
  gsap.ticker.lagSmoothing(0)
}

export function getLenis () {
  return lenis
}

export function lockScroll (locked) {
  if (lenis) locked ? lenis.stop() : lenis.start()
  document.body.classList.toggle('is-locked', locked)
}

/** 平滑滚动到指定区块 id */
export function scrollToId (id, { immediate = false } = {}) {
  const el = id === 'top' ? 0 : document.getElementById(id)
  if (el === null) return
  if (lenis) {
    // 各区块自带较大上内边距，顶部对齐即可，导航栏浮在内边距之上
    lenis.scrollTo(el, { duration: immediate ? 0 : 1.4, immediate })
  } else if (el === 0) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

/* ------------------------------------------------------------------
   指令
   ------------------------------------------------------------------ */

const revealObserver =
  typeof window !== 'undefined' &&
  new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in')
          revealObserver.unobserve(entry.target)
        }
      })
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  )

/** v-reveal="延迟秒数"：进入视口时淡入上移去模糊 */
const reveal = {
  mounted (el, binding) {
    el.classList.add('reveal')
    if (binding.value) el.style.setProperty('--d', `${binding.value}s`)
    if (prefersReducedMotion) el.classList.add('is-in')
    else revealObserver.observe(el)
  },
  unmounted (el) {
    revealObserver && revealObserver.unobserve(el)
  }
}

/** v-spotlight：把鼠标位置写入 --mx / --my，配合 .spot 样式 */
const spotlight = {
  mounted (el) {
    el.classList.add('spot')
    if (!isFinePointer) return
    el._spot = (e) => {
      const r = el.getBoundingClientRect()
      el.style.setProperty('--mx', `${e.clientX - r.left}px`)
      el.style.setProperty('--my', `${e.clientY - r.top}px`)
    }
    el.addEventListener('pointermove', el._spot)
  },
  unmounted (el) {
    el._spot && el.removeEventListener('pointermove', el._spot)
  }
}

/** v-tilt="最大角度"：轻微 3D 倾斜 */
const tilt = {
  mounted (el, binding) {
    if (!isFinePointer || prefersReducedMotion) return
    const max = binding.value || 6
    const rx = gsap.quickTo(el, 'rotationX', { duration: 0.6, ease: 'power3.out' })
    const ry = gsap.quickTo(el, 'rotationY', { duration: 0.6, ease: 'power3.out' })
    gsap.set(el, { transformPerspective: 900 })
    el._tiltMove = (e) => {
      const r = el.getBoundingClientRect()
      const px = (e.clientX - r.left) / r.width - 0.5
      const py = (e.clientY - r.top) / r.height - 0.5
      rx(-py * max)
      ry(px * max)
    }
    el._tiltLeave = () => { rx(0); ry(0) }
    el.addEventListener('pointermove', el._tiltMove)
    el.addEventListener('pointerleave', el._tiltLeave)
  },
  unmounted (el) {
    if (!el._tiltMove) return
    el.removeEventListener('pointermove', el._tiltMove)
    el.removeEventListener('pointerleave', el._tiltLeave)
  }
}

/** v-magnetic="强度"：按钮被鼠标轻轻吸附 */
const magnetic = {
  mounted (el, binding) {
    if (!isFinePointer || prefersReducedMotion) return
    const strength = binding.value || 0.3
    const x = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'power3.out' })
    const y = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'power3.out' })
    el._magMove = (e) => {
      const r = el.getBoundingClientRect()
      x((e.clientX - (r.left + r.width / 2)) * strength)
      y((e.clientY - (r.top + r.height / 2)) * strength)
    }
    el._magLeave = () => {
      gsap.to(el, { x: 0, y: 0, duration: 0.9, ease: 'elastic.out(1, 0.4)' })
    }
    el.addEventListener('pointermove', el._magMove)
    el.addEventListener('pointerleave', el._magLeave)
  },
  unmounted (el) {
    if (!el._magMove) return
    el.removeEventListener('pointermove', el._magMove)
    el.removeEventListener('pointerleave', el._magLeave)
  }
}

export const motionDirectives = { reveal, spotlight, tilt, magnetic }

/** 数字滚动计数 */
export function countUp (el, to, { duration = 2, decimals = 0, delay = 0 } = {}) {
  const obj = { v: 0 }
  if (prefersReducedMotion) {
    el.textContent = to.toFixed(decimals)
    return
  }
  gsap.to(obj, {
    v: to,
    duration,
    delay,
    ease: 'power3.out',
    onUpdate: () => { el.textContent = obj.v.toFixed(decimals) }
  })
}
